import React, { useState, useEffect } from 'react';
import Card from '../components/common/Card';
import ActionMenu from '../components/common/ActionMenu';
import { CheckCircle2, Pencil, Power, Trash2 } from 'lucide-react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import api from '../services/api';

const ExamBodies = () => {
  const [examBodies, setExamBodies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingBody, setEditingBody] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchExamBodies();
  }, []);

  const fetchExamBodies = async () => {
    setLoading(true);
    try {
      // Use the correct endpoint - /api/exam-bodies
      const response = await api.get('/exam-bodies');
      setExamBodies(response.data);
    } catch (error) {
      console.error('Error fetching exam bodies:', error);
      toast.error('Failed to fetch exam bodies');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Exam body name is required');
      return;
    }

    setLoading(true);
    try {
      if (editingBody) {
        await api.put(`/exam-bodies/${editingBody.id}`, formData);
        toast.success('Exam body updated successfully');
      } else {
        await api.post('/exam-bodies', formData);
        toast.success('Exam body added successfully');
      }
      setShowForm(false);
      setEditingBody(null);
      setFormData({ name: '', code: '', is_active: true });
      fetchExamBodies();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving exam body');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (body) => {
    setEditingBody(body);
    setFormData({
      name: body.name,
      code: body.code || '',
      is_active: body.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete ${name}? This will also remove all levels associated with it.`)) return;

    setLoading(true);
    try {
      await api.delete(`/exam-bodies/${id}`);
      toast.success('Exam body deleted successfully');
      fetchExamBodies();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting exam body');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (body) => {
    try {
      await api.put(`/exam-bodies/${body.id}`, {
        ...body,
        is_active: !body.is_active
      });
      toast.success(`Exam body ${!body.is_active ? 'activated' : 'deactivated'} successfully`);
      fetchExamBodies();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Exam Bodies</h1>
          <p className="text-gray-600 mt-1">
            Manage exam bodies like TVET-CDACC, KNEC, NITA, etc.
          </p>
        </div>
        <Button onClick={() => setShowForm(true)}>+ Add Exam Body</Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingBody ? 'Edit Exam Body' : 'Add New Exam Body'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Exam Body Name *"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Kenya National Examinations Council"
                required
              />
              <Input
                label="Code (Optional)"
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                placeholder="e.g., KNEC, TVET-CDACC, NITA"
              />
            </div>
            <div className="mt-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
            </div>
            <div className="flex gap-3 mt-4">
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : (editingBody ? 'Update' : 'Add Exam Body')}
              </Button>
              <Button variant="outline" onClick={() => {
                setShowForm(false);
                setEditingBody(null);
                setFormData({ name: '', code: '', is_active: true });
              }}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg overflow-hidden shadow">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
             </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {examBodies.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  No exam bodies found. Click "Add Exam Body" to create one.
                </td>
              </tr>
            ) : (
              examBodies.map((body) => (
                <tr key={body.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{body.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    {body.code ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {body.code}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleToggleActive(body)}
                      className={`px-2 py-1 text-xs rounded ${
                        body.is_active 
                          ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {body.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(body.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <ActionMenu
                      label={`Actions for ${body.name || 'exam body'}`}
                      items={[
                        {
                          label: body.is_active ? 'Deactivate' : 'Activate',
                          icon: Power,
                          onClick: () => handleToggleActive(body),
                        },
                        { label: 'Edit', icon: Pencil, onClick: () => handleEdit(body) },
                        {
                          label: 'Delete',
                          icon: Trash2,
                          variant: 'danger',
                          onClick: () => handleDelete(body.id, body.name),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExamBodies;
